import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RateDetail } from '../../models/ParkingRate.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimeBlock {
  id: number;
  days: number[];
  start: number;
  end: number;
}

export interface RateGroup<T = RateDetail> {
  id: number;
  color: string;
  rate: T | null;
  blocks: TimeBlock[];
}

export interface DragRect {
  minSlot: number;
  maxSlot: number;
  minDay: number;
  maxDay: number;
  mode: 'add' | 'remove';
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const PALETTE = [
  '#2563eb',
  '#059669',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#e6b822',
  '#41387c',
];

export const SPEC_COLORS = [
  '#d97706',
  '#2563eb',
  '#dc2626',
  '#7c3aed',
  '#059669',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#ea580c',
  '#4f46e5',
];

let _uid = 0;
export const uid = () => ++_uid;

// ─── Block merger ─────────────────────────────────────────────────────────────

export function mergeAdjacentBlocks(blocks: TimeBlock[]): TimeBlock[] {
  const dayRanges = new Map<number, Array<{ start: number; end: number }>>();
  blocks.forEach((b) => {
    b.days.forEach((d) => {
      if (!dayRanges.has(d)) dayRanges.set(d, []);
      dayRanges.get(d)!.push({ start: b.start, end: b.end });
    });
  });

  const mergedDayRanges = new Map<
    number,
    Array<{ start: number; end: number }>
  >();
  dayRanges.forEach((ranges, day) => {
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    const merged: Array<{ start: number; end: number }> = [];
    for (const r of sorted) {
      if (merged.length === 0) {
        merged.push({ ...r });
      } else {
        const last = merged[merged.length - 1];
        if (r.start <= last.end + 1) {
          last.end = Math.max(last.end, r.end);
        } else {
          merged.push({ ...r });
        }
      }
    }
    mergedDayRanges.set(day, merged);
  });

  const rangeTodays = new Map<string, Set<number>>();
  mergedDayRanges.forEach((ranges, day) => {
    ranges.forEach((r) => {
      const key = `${r.start}|${r.end}`;
      if (!rangeTodays.has(key)) rangeTodays.set(key, new Set());
      rangeTodays.get(key)!.add(day);
    });
  });

  return [...rangeTodays.entries()]
    .map(([key, days]) => {
      const [start, end] = key.split('|').map(Number);
      return { id: uid(), days: [...days].sort((a, b) => a - b), start, end };
    })
    .sort((a, b) => (a.days[0] ?? 0) - (b.days[0] ?? 0) || a.start - b.start);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseParkingRateGroupsOptions<T = RateDetail> {
  localRuleType: 'regular' | 'specific';
  regularDowSubset: number[];
  specialDates: string[];
  initialGroups?: RateGroup<T>[];
}

export interface UseParkingRateGroupsReturn<T = RateDetail> {
  groups: RateGroup<T>[];
  setGroups: React.Dispatch<React.SetStateAction<RateGroup<T>[]>>;
  editingGroupId: number | null;
  dragRect: DragRect | null;
  displayGroups: RateGroup<T>[];
  startEditGroup: (groupId: number) => void;
  addNewGroup: () => void;
  deleteGroup: (groupId: number) => void;
  deleteBlock: (groupId: number, blockId: number) => void;
  confirmGroupRate: (groupId: number, rate: T) => void;
  cancelGroupEdit: (groupId: number) => void;
  clearAll: () => void;
  handleCellDown: (day: number, slot: number) => void;
  handleCellEnter: (day: number, slot: number) => void;
}

export function useParkingRateGroups<T = RateDetail>({
  localRuleType,
  regularDowSubset,
  specialDates,
  initialGroups,
}: UseParkingRateGroupsOptions<T>): UseParkingRateGroupsReturn<T> {
  const [groups, setGroups] = useState<RateGroup<T>[]>(
    () => initialGroups ?? [],
  );
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [dragRect, setDragRect] = useState<DragRect | null>(null);

  // Refs needed by event handlers that are registered once via useEffect
  const groupsRef = useRef(groups);
  const editingGroupIdRef = useRef<number | null>(null);
  const groupSnapshotRef = useRef<RateGroup<T> | null>(null);
  const localRuleTypeRef = useRef(localRuleType);
  const dragModeRef = useRef<'add' | 'remove'>('add');
  const isDragging = useRef(false);
  const dDay = useRef(0);
  const dDayEnd = useRef(0);
  const dStart = useRef(0);
  const dEnd = useRef(0);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);
  useEffect(() => {
    editingGroupIdRef.current = editingGroupId;
  }, [editingGroupId]);
  useEffect(() => {
    localRuleTypeRef.current = localRuleType;
  }, [localRuleType]);

  // ── Pruning: remove blocks outside the valid column range ─────────────────

  // specific mode: prune blocks whose day index exceeds the date range
  useEffect(() => {
    if (localRuleType !== 'specific') return;
    const numDays = specialDates.length;
    if (numDays === 0) return;
    setGroups((prev) =>
      prev.map((g) => ({
        ...g,
        blocks: g.blocks
          .map((b) => ({ ...b, days: b.days.filter((d) => d < numDays) }))
          .filter((b) => b.days.length > 0),
      })),
    );
  }, [specialDates.length, localRuleType]);

  // regular mode: prune blocks outside the active DOW subset, remove emptied groups
  useEffect(() => {
    if (localRuleType !== 'regular') return;
    if (regularDowSubset.length === 7) return;
    const validCols = new Set(regularDowSubset.map((_, i) => i));
    setGroups((prev) =>
      prev
        .map((g) => ({
          ...g,
          blocks: g.blocks
            .map((b) => ({
              ...b,
              days: b.days.filter((d) => validCols.has(d)),
            }))
            .filter((b) => b.days.length > 0),
        }))
        .filter((g) => g.blocks.length > 0),
    );
  }, [regularDowSubset, localRuleType]);

  // ── Global mouseup: commit drag result ────────────────────────────────────

  useEffect(() => {
    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;

      const minSlot = Math.min(dStart.current, dEnd.current);
      const maxSlot = Math.max(dStart.current, dEnd.current);
      const minDay = Math.min(dDay.current, dDayEnd.current);
      const maxDay = Math.max(dDay.current, dDayEnd.current);
      const mode = dragModeRef.current;
      setDragRect(null);

      if (mode === 'remove') {
        const targetId = editingGroupIdRef.current;
        if (targetId === null) return;
        setGroups((prev) =>
          prev.map((g) => {
            if (g.id !== targetId) return g;
            const trimmed = g.blocks.flatMap((b) => {
              const timeOverlap = !(maxSlot < b.start || minSlot > b.end);
              if (!timeOverlap) return [b];
              const draggedDays = b.days.filter(
                (d) => d >= minDay && d <= maxDay,
              );
              const untouchedDays = b.days.filter(
                (d) => d < minDay || d > maxDay,
              );
              if (draggedDays.length === 0) return [b];
              const result: TimeBlock[] = [];
              if (untouchedDays.length > 0)
                result.push({ ...b, days: untouchedDays });
              if (b.start < minSlot)
                result.push({
                  id: uid(),
                  days: draggedDays,
                  start: b.start,
                  end: minSlot - 1,
                });
              if (b.end > maxSlot)
                result.push({
                  id: uid(),
                  days: draggedDays,
                  start: maxSlot + 1,
                  end: b.end,
                });
              return result;
            });
            return { ...g, blocks: mergeAdjacentBlocks(trimmed) };
          }),
        );
        return;
      }

      const targetId = editingGroupIdRef.current;
      if (targetId === null) return;

      const currentGs = groupsRef.current;
      const newDays: number[] = [];
      for (let d = minDay; d <= maxDay; d++) {
        const occupied = currentGs.some((g) =>
          g.blocks.some(
            (b) =>
              b.days.includes(d) && !(maxSlot < b.start || minSlot > b.end),
          ),
        );
        if (!occupied) newDays.push(d);
      }
      if (!newDays.length) return;

      setGroups((prev) =>
        prev.map((g) =>
          g.id === targetId
            ? {
                ...g,
                blocks: mergeAdjacentBlocks([
                  ...g.blocks,
                  { id: uid(), days: newDays, start: minSlot, end: maxSlot },
                ]),
              }
            : g,
        ),
      );
    };

    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, []);

  // ── Global mousemove: track drag position ─────────────────────────────────

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const el = document.elementFromPoint(
        e.clientX,
        e.clientY,
      ) as HTMLElement | null;
      if (!el) return;
      const dayStr = el.dataset.day;
      const slotStr = el.dataset.slot;
      if (dayStr === undefined || slotStr === undefined) return;
      dEnd.current = parseInt(slotStr, 10);
      dDayEnd.current = parseInt(dayStr, 10);
      setDragRect({
        minSlot: Math.min(dStart.current, dEnd.current),
        maxSlot: Math.max(dStart.current, dEnd.current),
        minDay: Math.min(dDay.current, dDayEnd.current),
        maxDay: Math.max(dDay.current, dDayEnd.current),
        mode: dragModeRef.current,
      });
    };
    document.addEventListener('mousemove', onMove);
    return () => document.removeEventListener('mousemove', onMove);
  }, []);

  // ── Cell event handlers ───────────────────────────────────────────────────

  const handleCellDown = useCallback((day: number, slot: number) => {
    if (editingGroupIdRef.current === null) return;

    const isOnOwnCell = groupsRef.current.some(
      (g) =>
        g.id === editingGroupIdRef.current &&
        g.blocks.some(
          (b) => b.days.includes(day) && slot >= b.start && slot <= b.end,
        ),
    );
    const mode: 'add' | 'remove' = isOnOwnCell ? 'remove' : 'add';
    dragModeRef.current = mode;
    isDragging.current = true;
    dDay.current = day;
    dDayEnd.current = day;
    dStart.current = slot;
    dEnd.current = slot;
    setDragRect({
      minSlot: slot,
      maxSlot: slot,
      minDay: day,
      maxDay: day,
      mode,
    });
  }, []);

  const handleCellEnter = useCallback((day: number, slot: number) => {
    if (!isDragging.current) return;
    dEnd.current = slot;
    dDayEnd.current = day;
    setDragRect({
      minSlot: Math.min(dStart.current, dEnd.current),
      maxSlot: Math.max(dStart.current, dEnd.current),
      minDay: Math.min(dDay.current, dDayEnd.current),
      maxDay: Math.max(dDay.current, dDayEnd.current),
      mode: dragModeRef.current,
    });
  }, []);

  // ── Group CRUD ────────────────────────────────────────────────────────────

  const startEditGroup = useCallback((groupId: number) => {
    const g = groupsRef.current.find((gr) => gr.id === groupId);
    groupSnapshotRef.current = g
      ? { ...g, blocks: g.blocks.map((b) => ({ ...b, days: [...b.days] })) }
      : null;
    setEditingGroupId(groupId);
  }, []);

  const addNewGroup = useCallback(() => {
    const isSpecial = localRuleTypeRef.current === 'specific';
    const palette = isSpecial ? SPEC_COLORS : PALETTE;
    const newGroup: RateGroup<T> = {
      id: uid(),
      color: palette[groupsRef.current.length % palette.length],
      rate: null,
      blocks: [],
    };
    groupSnapshotRef.current = { ...newGroup, blocks: [] };
    setGroups((prev) => [...prev, newGroup]);
    setEditingGroupId(newGroup.id);
  }, []);

  // 用 useCallback 讓這些 group-level callback 在多數情況下維持穩定的函式參照，
  // 搭配 ParkingRateGroupCard 的 React.memo 才有實際效果（否則呼叫端每次都要重新
  // 包一層新的箭頭函式，memo 的淺比較永遠判定 props 有變、永遠不會跳過重繪）。
  const deleteGroup = useCallback(
    (groupId: number) => {
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
      if (editingGroupId === groupId) setEditingGroupId(null);
    },
    [editingGroupId],
  );

  const deleteBlock = (groupId: number, blockId: number) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, blocks: g.blocks.filter((b) => b.id !== blockId) }
          : g,
      ),
    );
  };

  const confirmGroupRate = useCallback((groupId: number, rate: T) => {
    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, rate } : g)),
    );
    setEditingGroupId(null);
  }, []);

  const cancelGroupEdit = useCallback((groupId: number) => {
    const snapshot = groupSnapshotRef.current;
    groupSnapshotRef.current = null;
    if (snapshot && snapshot.id === groupId) {
      if (snapshot.blocks.length === 0) {
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
      } else {
        setGroups((prev) => prev.map((g) => (g.id === groupId ? snapshot : g)));
      }
    }
    setEditingGroupId(null);
  }, []);

  const clearAll = () => {
    setGroups([]);
    setEditingGroupId(null);
  };

  // ── displayGroups: synchronously-clipped version for safe rendering ───────
  //
  // The pruning useEffects above run after render. If the date range shrinks
  // And we render WeekGrid before the effect fires, block indices can go
  // out of bounds. displayGroups clips in the same render cycle to prevent that.

  const displayGroups = useMemo(() => {
    const keepEmpty = (g: RateGroup<T>) =>
      g.blocks.length > 0 || g.id === editingGroupId;

    if (localRuleType === 'regular') {
      if (regularDowSubset.length === 7) return groups;
      const validCols = new Set(regularDowSubset.map((_, i) => i));
      return groups
        .map((g) => ({
          ...g,
          blocks: g.blocks
            .map((b) => ({
              ...b,
              days: b.days.filter((d) => validCols.has(d)),
            }))
            .filter((b) => b.days.length > 0),
        }))
        .filter(keepEmpty);
    } else {
      const maxDay = specialDates.length;
      if (maxDay === 0) return [];
      return groups
        .map((g) => ({
          ...g,
          blocks: g.blocks
            .map((b) => ({ ...b, days: b.days.filter((d) => d < maxDay) }))
            .filter((b) => b.days.length > 0),
        }))
        .filter(keepEmpty);
    }
  }, [groups, localRuleType, regularDowSubset, specialDates, editingGroupId]);

  return {
    groups,
    setGroups,
    editingGroupId,
    dragRect,
    displayGroups,
    startEditGroup,
    addNewGroup,
    deleteGroup,
    deleteBlock,
    confirmGroupRate,
    cancelGroupEdit,
    clearAll,
    handleCellDown,
    handleCellEnter,
  };
}
